import * as React from 'react';
import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import _ from 'lodash';
import { widget } from 'charting_library';
import { getCandleStickData } from 'store/exchange/chart/chartThunk';
import { getSocket } from 'middleware/socketManager';
import { socketStatus } from 'store/exchange/socket/socketSlice';
import { getPriceMaxDigits } from 'view/router/exchange/util/numberUtil';
import { BigNumber } from 'bignumber.js';
import { getCurrentPrice } from 'store/exchange/pairs/pairsThunk';
import toast from 'react-hot-toast';
import { TradeContext } from '../../context/TradeContext';
import {MAIN_DOWN_COLOR, MAIN_UP_COLOR, SUB_DOWN_COLOR, SUB_UP_COLOR} from "../../util/literal";
import {selectAllCurrencies} from "store/exchange/currencies/currenciesSlice";

const LINESTYLE_SOLID = 0;
const LINESTYLE_DOTTED = 1;
const LINESTYLE_DASHED = 2;
const LINESTYLE_LARGE_DASHED = 3;

const configurationData = {
  exchanges: [],
  symbols_types: [],
  supported_resolutions: ['1', '3', '5', '15', '30', '60', '240', '1D', '1W'],
};

// resolution 을 서버에 보내는 형태로 맞추기 위한 함수
function changeResolutionToUnit(resolution) {
  switch (resolution) {
    case '1':
      return '1m';
    case '3':
      return '3m';
    case '5':
      return '5m';
    case '15':
      return '15m';
    case '30':
      return '30m';
    case '60':
      return '1h';
    case '240':
      return '4h';
    case '1D':
      return '1d';
    case '1W':
      return '1w';
    default:
      return '';
  }
}

// trading-view 에 표시되는대로 key 값 조절
function makeChartData(from, to, resolution, data) {
  return data.map((el) => ({
    time: el.start_time,
    open: el.open * 1,
    close: el.close * 1,
    high: el.high * 1,
    low: el.low * 1,
    volume: el.volume * 1,
  }));
}

const channelToSubscription = new Map();
const lastBarsCache = new Map();

function TVChartContainer() {
  /* --- redux, context, props, hook 등으로 받은 전역값 --- */
  const dispatch = useDispatch();

  const { pair, setPrice, setTradable } = useContext(TradeContext);

  const {
    symbol, market, id, price,
  } = pair || {};

  const socket = getSocket();
  const reduxSocketStatus = useSelector(socketStatus);

  const currencies = useSelector(selectAllCurrencies);
  const symbolCurrencyName = currencies.find((el) => el.symbol === symbol)?.name;
  const marketCurrencyName = currencies.find((el) => el.symbol === market)?.name;
  /* -------------------------------------------------------------------------------------------- */

  /* --- widget state : api를 등록해뒀기 때문에 tvWidget.api 형태로 사용하면 됨 --- */
  const [tvWidget, setTvWidget] = useState(null);
  const twWidgetRef = useRef();
  /* -------------------------------------------------------------------------------------------- */

  /* --- 유저가 interval 변경 시 소켓 구독 및 해제 관련 처리 --- */
  const subscribeOnStream = useCallback((
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscribeUID,
    onResetCacheNeededCallback,
    lastDailyBar,
  ) => {
    const handler = {
      id: subscribeUID,
      callback: onRealtimeCallback,
    };
    let subscriptionItem = channelToSubscription.get(symbolInfo.full_name);
    if (subscriptionItem) {
      // already subscribed to the channel, use the existing subscription
      subscriptionItem.subscribeUID = subscribeUID;
      subscriptionItem.resolution = resolution;
      subscriptionItem.handlers.push(handler);
    } else {
      subscriptionItem = {
        subscribeUID,
        resolution,
        lastDailyBar,
        handlers: [handler],
      };
    }
    channelToSubscription.set(symbolInfo.full_name, subscriptionItem);
    // console.log('[subscribeBars]: Subscribe to streaming. Channel:', symbol);
    if (reduxSocketStatus === 'OPENED' && socket) {
      // console.log('[TVChartContainer]::joinRoom', `dex_candlestick_${id}_${changeResolutionToUnit(resolution)}`);
      socket.emit('joinRoom', { roomName: `dex_candlestick_${id}_${changeResolutionToUnit(resolution)}` });
    }
  }, [socket, reduxSocketStatus, symbol, market, id]);

  const unsubscribeFromStream = useCallback((subscribeUID) => {
    const unit = changeResolutionToUnit(_.head(_.takeRight(subscribeUID.split('_'))));
    if (reduxSocketStatus === 'OPENED' && socket && socket.connected && id && unit) {
      socket.emit('leaveRoom', { roomName: `dex_candlestick_${id}_${unit}` });
    }
    const subscriptionItem = channelToSubscription.get(`Volare DEX:${symbol}/${market}`);
    if (subscriptionItem) {
      // already subscribed to the channel, use the existing subscription
      subscriptionItem.handlers = subscriptionItem.handlers.filter((el) => el.id !== subscribeUID);
    }
  }, [socket, symbol, market, id, reduxSocketStatus]);
  /* -------------------------------------------------------------------------------------------- */

  /* --- Chart 데이터를 생성하는 부분에 관한 함수를 정의 --- */
  const datafeed = useMemo(() => ({
    onReady: (callback) => {
      // console.log('[onReady]: Method call');
      setTimeout(() => callback(configurationData));
    },
    searchSymbols: (userInput, exchange, symbolType, onResultReadyCallback) => {
      // console.log('[searchSymbols]: Method call');
    },
    resolveSymbol: (symbolName, onSymbolResolvedCallback, onResolveErrorCallback) => {
      // console.log('[resolveSymbol]: Method call');
      const symbolInfo = {
        // ticker: symbol,
        name: `${symbol}/${market}`,
        description: `${symbolCurrencyName}/${marketCurrencyName}`,
        type: 'crypto',
        session: '24x7',
        timezone: 'UTC',
        exchange: 'Volare DEX',
        listed_exchange: 'Volare DEX',
        minmov: 1,
        pricescale: 10 ** getPriceMaxDigits(price * 1),
        has_intraday: true,
        has_weekly_and_monthly: true,
        has_daily: true,
        has_no_volume: false,
        supported_resolutions: configurationData.supported_resolutions,
        volume_precision: 8,
        data_status: 'streaming',
      };
      setTimeout(() => onSymbolResolvedCallback(symbolInfo));
    },
    getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
      // console.log('[getBars]: Method call', symbolInfo, resolution, periodParams);
      try {
        const {
          countBack, firstDataRequest, from, to,
        } = periodParams;
        const result = unwrapResult(await dispatch(
          getCandleStickData({
            unit: changeResolutionToUnit(resolution),
            pairId: id,
            count: countBack,
            ...(!firstDataRequest && { endTime: to * 1000 }),
          }),
        ));
        if (result && result.candles && result.candles.length === 0) {
          onHistoryCallback([], { noData: true });
          return;
        }
        const bars = makeChartData(
          from,
          to,
          changeResolutionToUnit(resolution),
          [...result.candles].reverse(),
        );
        if (firstDataRequest) {
          lastBarsCache.set(symbolInfo.full_name, {
            ...bars[bars.length - 1],
          });
        }
        onHistoryCallback(
          bars,
          { noData: false },
        );
      } catch (error) {
        // console.error('[getBars]: Get error', error);
        toast('something wrong during getting charts... please try again', { icon: '❌' });
        setTradable(false);
        onErrorCallback(error);
      }
    },
    subscribeBars: (
      symbolInfo,
      resolution,
      onRealtimeCallback,
      subscribeUID,
      onResetCacheNeededCallback,
    ) => {
      // console.log('[subscribeBars]: Method call with subscribeUID:', subscribeUID);
      subscribeOnStream(
        symbolInfo,
        resolution,
        onRealtimeCallback,
        subscribeUID,
        onResetCacheNeededCallback,
        lastBarsCache.get(symbolInfo.full_name),
      );
    },
    unsubscribeBars: (subscriberUID) => {
      // console.log('[unsubscribeBars]: Method call with subscriberUID:', subscriberUID);
      unsubscribeFromStream(subscriberUID);
    },
  }), [dispatch, symbol, market, subscribeOnStream, unsubscribeFromStream, price, symbolCurrencyName, marketCurrencyName]);
  /* -------------------------------------------------------------------------------------------- */

  /* --- widget-option 을 통해 트레이딩뷰 UI 조절 --- */
  const widgetOptions = useMemo(() => ({
    symbol,
    datafeed,
    interval: '1',
    container: 'tv_chart_container',
    library_path: '/charting_library/',
    locale: 'en',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    disabled_features: ['header_symbol_search', 'header_compare', 'header_undo_redo', 'header_screenshot', 'header_saveload', 'show_object_tree', 'timeframes_toolbar'],
    enabled_features: [],
    charts_storage_url: 'https://saveload.tradingview.com',
    charts_storage_api_version: '1.1',
    client_id: 'tradingview.com',
    user_id: 'public_user_id',
    fullscreen: false,
    autosize: true,
    custom_css_url: '../widget.css',
    toolbar_bg: '#141313',
    favorites: {
      intervals: ['1', '3', '5', '15', '30', '60', '240', '1D', '1W'],
    },
    theme: 'Dark',
    overrides: {
      editorFontsList: ['DINPro', 'Verdana', 'Courier New', 'Times New Roman', 'Arial'],
      'paneProperties.background': '#141313',
      'paneProperties.backgroundType': 'solid',
      'paneProperties.vertGridProperties.color': '#363636',
      'paneProperties.vertGridProperties.style': LINESTYLE_DASHED,
      'paneProperties.horzGridProperties.color': '#363636',
      'paneProperties.horzGridProperties.style': LINESTYLE_DASHED,

      'scalesProperties.textColor': '#afbbcf',

      'mainSeriesProperties.candleStyle.upColor': MAIN_UP_COLOR,
      'mainSeriesProperties.candleStyle.downColor': MAIN_DOWN_COLOR,
      'mainSeriesProperties.candleStyle.borderColor': MAIN_UP_COLOR,
      'mainSeriesProperties.candleStyle.borderUpColor': MAIN_UP_COLOR,
      'mainSeriesProperties.candleStyle.borderDownColor': MAIN_DOWN_COLOR,
      'mainSeriesProperties.candleStyle.wickUpColor': MAIN_UP_COLOR,
      'mainSeriesProperties.candleStyle.wickDownColor': MAIN_DOWN_COLOR,

      'mainSeriesProperties.hollowCandleStyle.upColor': MAIN_UP_COLOR,
      'mainSeriesProperties.hollowCandleStyle.downColor': MAIN_DOWN_COLOR,
      'mainSeriesProperties.hollowCandleStyle.borderUpColor': MAIN_UP_COLOR,
      'mainSeriesProperties.hollowCandleStyle.borderDownColor': MAIN_DOWN_COLOR,
      'mainSeriesProperties.hollowCandleStyle.wickUpColor': MAIN_UP_COLOR,
      'mainSeriesProperties.hollowCandleStyle.wickDownColor': MAIN_DOWN_COLOR,

      'mainSeriesProperties.haStyle.upColor': MAIN_UP_COLOR,
      'mainSeriesProperties.haStyle.downColor': MAIN_DOWN_COLOR,
      'mainSeriesProperties.haStyle.borderUpColor': MAIN_UP_COLOR,
      'mainSeriesProperties.haStyle.borderDownColor': MAIN_DOWN_COLOR,
      'mainSeriesProperties.haStyle.wickUpColor': MAIN_UP_COLOR,
      'mainSeriesProperties.haStyle.wickDownColor': MAIN_DOWN_COLOR,

      'mainSeriesProperties.barStyle.upColor': MAIN_UP_COLOR,
      'mainSeriesProperties.barStyle.downColor': MAIN_DOWN_COLOR,

      'mainSeriesProperties.baselineStyle.topFillColor1': 'rgba( 38, 87, 220, 0.28)',
      'mainSeriesProperties.baselineStyle.topFillColor2': 'rgba( 38, 87, 220, 0.05)',
      'mainSeriesProperties.baselineStyle.bottomFillColor1': 'rgba( 255, 0, 0, 0.05)',
      'mainSeriesProperties.baselineStyle.bottomFillColor2': 'rgba( 255, 0 ,0, 0.28)',
      'mainSeriesProperties.baselineStyle.topLineColor': MAIN_UP_COLOR,
      'mainSeriesProperties.baselineStyle.bottomLineColor': MAIN_DOWN_COLOR,

    },
    studies_overrides: {
      'volume.volume.color.0': SUB_DOWN_COLOR,
      'volume.volume.color.1': SUB_UP_COLOR,
    },
  }), [symbol, datafeed]);
  /* -------------------------------------------------------------------------------------------- */

  /* --- chart 데이터를 정상적으로 표기하기 위해, price 데이터를 먼저 가져옴 --- */
  const [tvChartReady, setTvChartReady] = useState(false);

  const setDailyInfoToRedux = useCallback(async () => {
    const result = unwrapResult(
      await dispatch(
        getCurrentPrice({
          pairIds: [id],
        }),
      ),
    );
    const { price } = _.head(result) || {};
    setPrice(new BigNumber(price).toNumber());
  }, [dispatch, id]);

  useEffect(() => {
    if (symbol && market && id) {
      setDailyInfoToRedux().catch((e) => {
        // console.error('[Trade]::setDailyInfoToRedux:', e);
        toast('something wrong during getting data... please try again', { icon: '❌' });
        setTradable(false);
      }).finally(
        (res) => {
          setTvChartReady(true);
        },
      );
    }
  }, [id, symbol, market]);

  useEffect(() => () => {
    setTvChartReady(false);
  }, [pair]);

  // new 를 통해 새로운 widget 을 생성하면, 하단의 div 가 이를 읽어서 처리
  useEffect(() => {
    if (tvChartReady) {
      // eslint-disable-next-line new-cap, no-new
      new widget(widgetOptions);
    }
  }, [tvChartReady, reduxSocketStatus]);
  /* -------------------------------------------------------------------------------------------- */

  /* --- 유저가 interval 변경 시 소켓 구독 및 해제 관련 처리 --- */
  useEffect(() => {
    if (reduxSocketStatus === 'OPENED' && socket) {
      socket.on('getCandlestickData', (data) => {
        // console.log('[socket] Message:', data);
        const subscriptionItem = channelToSubscription.get(`Volare DEX:${symbol}/${market}`);
        if (subscriptionItem === undefined) {
          return;
        }
        const { candlestick, pair_id: pairId, unit } = data;
        const bars = candlestick.map((el) => ({
          time: el.start_time * 1,
          close: el.close * 1,
          open: el.open * 1,
          low: el.low * 1,
          high: el.high * 1,
          volume: el.volume * 1,
        }));
        subscriptionItem.lastDailyBar = _.head(_.takeRight(bars));
        // send data to every subscriber of that symbol
        bars.forEach((bar) => {
          subscriptionItem.handlers.forEach((handler) => {
            if (changeResolutionToUnit(_.head(_.takeRight(handler.id.split('_')))) === unit) {
              handler.callback(bar);
            }
          });
        });
      });
    }
    return () => {
      if (socket) {
        socket.off('getCandlestickData');
      }
      const subscriptionItem = channelToSubscription.get(`Volare DEX:${symbol}/${market}`);
      if (subscriptionItem) {
        const parsedSubscribeUID = subscriptionItem.subscribeUID.split('_');
        const unit = changeResolutionToUnit(_.head(_.takeRight(parsedSubscribeUID)));
        if (socket && socket.connected && reduxSocketStatus === 'OPENED' && id && unit) {
          socket.emit('leaveRoom', { roomName: `dex_candlestick_${id}_${unit}` });
          // console.log(`socket leaveRoom dex_candlestick_${id}_${unit}`);
        }
      }
    };
  }, [reduxSocketStatus, socket, symbol, market, id]);

  /* unmount 시 초기화 */
  useEffect(() => () => {
    if (tvWidget) {
      tvWidget.remove();
      setTvWidget(null);
    }
  }, [tvWidget]);

  return <div ref={twWidgetRef} id="tv_chart_container" className="tv-container-chart" />;
}

export default TVChartContainer;
